<script setup lang="ts">
import { ref, onMounted } from 'vue'
import AppModal from '@/components/ui/AppModal.vue'
import { useRegistrationStatusesStore, type RegistrationStatus } from '@/domains/home/services/nir/configurations/stores/registrationStatuses.ts'
import { useInstitutionStore } from '@/domains/home/services/nir/associate-registers/stores'

const store = useRegistrationStatusesStore()
const institutionStore = useInstitutionStore()

const loading = ref(false)
const institutionFilter = ref('')
const modalOpen = ref(false)
const editingId = ref('')

const form = ref({
  institutionId: '',
  status: '',
  description: '',
})

onMounted(() => {
  institutionStore.fetchInstitutions(1, 1000)
  fetch()
})

function fetch() {
  loading.value = true
  store.fetchRegistrationStatuses(1, 100, institutionFilter.value || undefined).then(() => {
    loading.value = false
  })
}

function openCreate() {
  editingId.value = ''
  form.value = { institutionId: '', status: '', description: '' }
  modalOpen.value = true
}

function openEdit(s: RegistrationStatus) {
  editingId.value = s.id
  form.value = {
    institutionId: s.institutionId ?? '',
    status: s.status,
    description: s.description,
  }
  modalOpen.value = true
}

function save() {
  loading.value = true
  const action = editingId.value
    ? store.updateRegistrationStatus({ id: editingId.value, status: form.value.status, description: form.value.description })
    : store.createRegistrationStatus(form.value)

  action.then(() => {
    modalOpen.value = false
    fetch()
  }).finally(() => {
    loading.value = false
  })
}

function institutionName(id: string | null) {
  if (!id) return '—'
  return institutionStore.institutions?.find((i) => i.id === id)?.name ?? id
}
</script>

<template>
  <div class="flex-1 flex flex-col">
    <!-- Action Bar -->
    <div class="flex items-center justify-between mb-4 pt-4 px-4">
      <div class="flex items-center gap-2">
        <select
          v-model="institutionFilter"
          @change="fetch"
          class="px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary"
        >
          <option value="">All Institutions</option>
          <option
            v-for="inst in institutionStore.institutions"
            :key="inst.id"
            :value="inst.id"
          >
            {{ inst.name }}
          </option>
        </select>
      </div>

      <button @click="openCreate" class="btn-primary flex items-center gap-2">
        <i class="fa-solid fa-plus"></i>
        <span>Add Status</span>
      </button>
    </div>

    <!-- Table -->
    <div class="bg-white rounded shadow mx-4 flex flex-col">
      <div class="overflow-x-auto flex-1">
        <table class="min-w-full text-xs">
          <thead class="bg-gray-50 sticky top-0">
            <tr>
              <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200 w-12">#</th>
              <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Status</th>
              <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Description</th>
              <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Institution</th>
              <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Created At</th>
              <th class="text-center py-3 px-4 font-semibold text-gray-700 border-b border-gray-200 w-24">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(item, idx) in store.registrationStatuses"
              :key="item.id"
              class="hover:bg-gray-50 transition"
            >
              <td class="py-3 px-4 border-b border-gray-200 font-semibold">{{ idx + 1 }}</td>
              <td class="py-3 px-4 border-b border-gray-200">
                <span class="px-2 py-0.5 rounded-full text-xs font-semibold bg-primary-100 text-primary">
                  {{ item.status.replace(/_/g, " ") }}
                </span>
              </td>
              <td class="py-3 px-4 border-b border-gray-200 text-gray-600">{{ item.description || '—' }}</td>
              <td class="py-3 px-4 border-b border-gray-200 text-gray-600">{{ institutionName(item.institutionId) }}</td>
              <td class="py-3 px-4 border-b border-gray-200 text-gray-500">
                {{ item.createdAt ? new Date(item.createdAt).toLocaleDateString() : '—' }}
              </td>
              <td class="py-3 px-4 border-b border-gray-200 text-center">
                <button
                  @click="openEdit(item)"
                  class="btn-sm-primary"
                  title="Edit"
                >
                  <i class="fa-solid fa-pen"></i>
                </button>
              </td>
            </tr>
            <tr v-if="!store.registrationStatuses || store.registrationStatuses.length === 0">
              <td colspan="6" class="py-10 text-center text-gray-400">
                <i class="fa-solid fa-circle-info text-2xl mb-2 block"></i>
                No registration statuses found.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Create / Edit Modal -->
    <AppModal v-model="modalOpen" xl2>
      <div class="p-4">
        <h2 class="text-lg font-bold text-gray-900 mb-4">
          {{ editingId ? 'Edit' : 'Add' }} Registration Status
        </h2>

        <form @submit.prevent="save" class="space-y-4">
          <!-- Institution (only for create) -->
          <div v-if="!editingId">
            <label class="block text-sm font-medium text-gray-700 mb-1.5">
              <i class="fa-solid fa-landmark text-gray-400 mr-1"></i>
              Institution
            </label>
            <select
              v-model="form.institutionId"
              class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary"
            >
              <option value="">Select Institution</option>
              <option
                v-for="inst in institutionStore.institutions"
                :key="inst.id"
                :value="inst.id"
              >
                {{ inst.name }} ({{ inst.code }})
              </option>
            </select>
          </div>

          <!-- Status -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1.5">
              <i class="fa-solid fa-tag text-gray-400 mr-1"></i>
              Status *
            </label>
            <input
              v-model="form.status"
              @input="form.status = (($event.target as HTMLInputElement).value).replace(/ /g, '_')"
              type="text"
              required
              class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary"
              placeholder="e.g. COMPLIANT"
            />
          </div>

          <!-- Description -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1.5">
              <i class="fa-solid fa-align-left text-gray-400 mr-1"></i>
              Description
            </label>
            <textarea
              v-model="form.description"
              rows="3"
              class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary"
              placeholder="Brief description of this status"
            ></textarea>
          </div>

          <div class="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
            <button type="button" @click="modalOpen = false" class="btn-danger">
              <i class="fas fa-times mr-1"></i> Cancel
            </button>
            <button type="submit" :disabled="loading" class="btn-primary disabled:opacity-50">
              <i class="fa-solid fa-check mr-1"></i>
              {{ loading ? 'Saving…' : editingId ? 'Update Status' : 'Create Status' }}
            </button>
          </div>
        </form>
      </div>
    </AppModal>
  </div>
</template>

<style scoped>
table {
  border-collapse: separate;
  border-spacing: 0;
}
</style>
